// The Productivity Chart's Highcharts options, as one pure function of what is currently
// on screen: no reactivity, no chart instance, nothing read from anywhere but its own arguments.
// The chrome constants and sizing helpers the panel needs to reserve room for the chart live here
// too, since they are the same measurements the options are built from.

import merge from 'lodash-es/merge';
import type { Chart, Options } from 'highcharts';
import { verticalFade } from '@/plugins/highcharts/chartColors';
import { i18n } from '@/plugins/internationalization/i18n';
import type { ProductivityGranularity, ProductivityRange } from '@/plugins/store/actions/queries/active-groups';
import type { AlarmMarker, CameraSeries, FeedMixBlock, NoteMarker, ProductivityBucket, ProductivityProductionGroup, WeightSeriesDay } from '@/types/SectionLiveKpi';
import { aggregateEmphasis, cameraEmphasis, cameraSeriesKey, cameraSeriesName, cameraSeriesStyle, drawsCameraSeries, isCameraMetric } from './cameraSeries';
import { productivityAxisLabelFormatter, productivityTickPositioner, type ComparisonProjection } from './productivityAxis';
import { buildCalendarRulePlotLines } from './productivityAxisRules';
import type { BandBox, BandLayout } from './productivityBands';
import { CHART_AXIS_RULE_COLORS, CHART_CHROME_COLORS, CHART_REFERENCE_COLORS, CHART_SERIES_COLORS } from './productivityColors';
import { buildEventSeries, buildFeedMixSeries, buildProductionGroupSeries } from './productivityContextBands';
import type { ProductivityEventTooltip } from './productivityEventTooltip';
import { bucketTimestamp } from './productivityFormatting';
import { METRIC_KEYS, PRODUCTIVITY_AXIS, PRODUCTIVITY_METRICS, waterActualKey, type MetricBandId, type MetricFlags, type MetricKey } from './productivityMetrics';
import {
    buildDailyGainSeriesData,
    buildDeadCountSeriesData,
    buildGrowthIndexSeriesData,
    buildWaterActualSeriesData,
    buildWaterNormSeriesData,
    buildWaterOverNormSeriesData,
    buildWeightSeriesData,
    buildCameraMetricSeriesData,
    isolatedPointMarkers,
    metricColumnRange,
    waterColumnRange,
} from './productivitySeries';

// The panel fills the overlay, so the chart takes whatever height the surrounding chrome - page
// header, overlay header, this panel's own toolbar and readout row, padding - leaves it. A fixed
// height instead strands a screenful of white space under the bands on a tall window and crushes
// them into unreadable slivers on a short one; the bands themselves are proportional
// (computeBandLayout), so every one of them gains or loses room together.
const CHART_CHROME_PX = 430;
const CHART_MIN_HEIGHT_PX = 440;
const CHART_MAX_HEIGHT_PX = 1200;
// The caveat lines under the chart are part of that chrome, but only when there are any - so they
// are measured rather than folded into the constant. Left out, the chart claimed the whole panel
// and the caveat was pushed through its bottom border and off the window, which is exactly the
// text a reader must not miss: it is the only thing explaining a gap in a curve.
const CAVEAT_LINE_PX = 18;
const CAVEAT_MARGIN_PX = 6;

// What the bands actually get to divide up: the chart's height less the navigator strip and its
// margin, the x axis' labels, and the chart's own vertical spacing. computeBandLayout needs it to
// express its fixed-pixel context bands as the percentages Highcharts' axis `top`/`height` take.
// An estimate rather than a measurement, because the layout is decided before the chart that would
// report it exists - and the axes it feeds are themselves what would change the answer.
const CHART_NON_PLOT_PX = 108;

export function productivityChartHeight(windowHeightPx: number, caveatLineCount: number): number {
    const caveatHeightPx = caveatLineCount > 0 ? CAVEAT_MARGIN_PX + caveatLineCount * CAVEAT_LINE_PX : 0;
    return Math.min(Math.max(windowHeightPx - CHART_CHROME_PX - caveatHeightPx, CHART_MIN_HEIGHT_PX), CHART_MAX_HEIGHT_PX);
}

export function productivityPlotHeight(chartHeightPx: number): number {
    return Math.max(chartHeightPx - CHART_NON_PLOT_PX, 1);
}

// A band with no box (hidden by its toggle, its sibling's toggle, or Sensor gating) collapses to
// zero height and stays invisible - never rendered as an empty sliver - so whatever space it
// would have taken is left for computeBandLayout to hand to the bands that remain.
type AxisBox = { top: string; height: string; visible: boolean };

function axisBox(box: BandBox | null): AxisBox {
    return box ? { top: `${box.top}%`, height: `${box.height}%`, visible: true } : { top: '0%', height: '0%', visible: false };
}

// A band's caption, drawn horizontally at its own top-left corner rather than as Highcharts' default
// rotated axis title - a rotated title is unreadable at the height a single band gets, and the
// bands are what the reader has to tell apart. Units come from the same strings the Section table's
// own cells use, so the two never disagree about what a figure is measured in.
// `inside` seats the caption below the axis top rather than above it. Needed by a band that has no
// gap above it to sit in - see FUSED_WITH_BAND_ABOVE - where the usual position lands the caption
// inside the *previous* band, on top of whatever that band is drawing. Only safe for a band whose
// own top-right corner is reliably empty, which a column series growing from zero is and a line
// series is not.
function bandTitle(text: string, opposite = false, inside = false): Record<string, unknown> {
    return {
        text,
        rotation: 0,
        align: 'high',
        // A caption anchored on the axis line grows away from the plot unless it is aligned back
        // towards it: left-hand axes read rightwards, the one right-hand axis reads leftwards, so
        // neither runs off its own edge of the panel.
        textAlign: opposite ? 'right' : 'left',
        // Zero collapses the axis-title margin Highcharts would otherwise reserve from the label
        // widths, so the caption anchors on the axis line itself and `x` can nudge it just inside
        // the plot rather than into the gutter the labels occupy.
        offset: 0,
        x: opposite ? -4 : 4,
        y: inside ? 13 : -8,
        // nowrap, because Highcharts sizes an axis title's wrap width from the axis it belongs to:
        // a short band gets a narrow allowance and breaks its caption's unit onto a second line,
        // straight through the topmost tick label.
        style: { fontSize: '11px', fontWeight: '600', color: CHART_CHROME_COLORS.labelText, whiteSpace: 'nowrap' },
    };
}

// A metric's caption: its own name plus what it is measured in, since a number beside a curve has
// no column heading to be read under. A metric that is not a measurement carries no unit.
function metricCaption(key: MetricKey, granularity: ProductivityGranularity): string {
    const label = i18n.global.t(PRODUCTIVITY_METRICS[key].labelKey) as string;
    const unitKey = PRODUCTIVITY_METRICS[key].unitKey(granularity);
    if (unitKey === null) return label;
    return i18n.global.t('views.monitoring.activeGroups.modals.productivity.metricCaption', {
        metric: label,
        unit: i18n.global.t(unitKey) as string,
    }) as string;
}

// Chrome shared by every data band's axis. tickPixelInterval rather than a fixed tick count: the
// bands are proportionally sized, so a band that shrinks (because a sibling was toggled on) must
// thin its own labels out instead of cramming the same number into less height.
// opposite: false is deliberate and must stay - Highcharts *Stock* puts y axes on the right by
// default, which lands each band's caption against the panel's right edge where it clips, and reads
// backwards against an x axis that starts its own count ("Dag 1") on the left.
const BAND_AXIS_CHROME = {
    opposite: false,
    // Pinned, because Highcharts otherwise staggers axes that share a side so their labels cannot
    // collide - a rule written for axes that overlap the same box. These do not: they are stacked
    // vertically into separate bands, so the stagger only walks each successive band's labels and
    // caption further off the left edge until they clip.
    offset: 0,
    labels: { style: { fontSize: '11px', color: CHART_CHROME_COLORS.axisLabel }, x: -8 },
    tickPixelInterval: 44,
    gridLineColor: CHART_CHROME_COLORS.bandGridLine,
    gridLineDashStyle: 'Solid',
    lineWidth: 0,
    showLastLabel: true,
    // Highcharts rounds an axis out to whole ticks at both ends, which on a band a fraction of the
    // chart's height routinely spends a third of it on empty scale. These bands are read for the
    // shape of a curve inside one of them, so the curve gets the room and the padding below keeps
    // it off the band's own edges.
    startOnTick: false,
    endOnTick: false,
    minPadding: 0.06,
    maxPadding: 0.12,
};

// Labels for the two bands that sit on the right-hand side. `align` is stated, not inherited:
// Highcharts Stock writes `align: 'right'` into its own y axis defaults, and `opposite: true` does
// not flip a default that has been written down - so with only `x: 8` given, these numbers anchor
// their *right* edge 8px inside the plot, over the water columns, over anything near the right edge,
// and over the plot's own frame. Left-aligned, `x: 8` reads as "8px into the gutter", which is what
// it is meant to say.
const OPPOSITE_BAND_LABELS = { ...BAND_AXIS_CHROME.labels, align: 'left', x: 8 };

/**
 * One data band's whole axis, with every option that varies with the band's own state stated on
 * every build.
 *
 * Nothing may be left out on the strength of "it is already right": Highcharts merges an options
 * update into what the axis already holds, so a band that empties and then fills again - which is
 * what changing granularity does to the water band, since water is charted at some granularities
 * and not others - would otherwise keep the disabled labels, the 0..1 extremes and the "no data"
 * note from when it was empty, and draw its restored series clipped against a scale that no longer
 * describes it.
 *
 * `null`, not `undefined`, for an extreme being handed back to the data: that is Highcharts' own
 * "derive this yourself" value, where undefined is simply ignored by the merge.
 */
function dataBandAxis(input: {
    box: AxisBox;
    visible: boolean;
    title: Record<string, unknown>;
    // A Sensor the Section has, that reported nothing across the selected range: the band still
    // draws (at a reduced height, carrying the note below) rather than vanishing, since "no reading"
    // and "reading is flat" are different answers and only the band can tell them apart. The note is
    // drawn as a full-width plot band label so it sits centred in that band's own box whatever
    // height the band ends up with, and the axis drops its own scale with it: an empty band's ticks
    // are an invented 0..1 that describes nothing, and reading like a real scale is worse than
    // showing none.
    isEmpty: boolean;
    // The note this band draws while empty, or null for an empty band that stays silent because
    // another band is already saying it - see `noteBands`.
    noReadingsLabel: string | null;
    // Right-hand side, for a band sharing its plot box with the one above it. On the left its
    // numbers would run into that band's own gutter.
    opposite?: boolean;
    // The floor this band's scale is anchored to whenever it holds data - null for a band fitted to
    // its own readings.
    min?: number | null;
    gridLineWidth?: number;
    // What only this band declares: a floor, a soft bound, its own reference line.
    own?: Record<string, unknown>;
}): Record<string, unknown> {
    const opposite = input.opposite ?? false;
    // Restated from the side the band sits on rather than inherited: a right-hand band's numbers are
    // anchored differently from a left-hand one's, and handing back the shared left-hand chrome puts
    // them back inside the plot, on top of whatever that band is drawing.
    const labels = opposite ? OPPOSITE_BAND_LABELS : BAND_AXIS_CHROME.labels;
    return {
        ...input.box,
        ...BAND_AXIS_CHROME,
        ...(input.own ?? {}),
        visible: input.visible,
        opposite,
        title: input.title,
        labels: input.isEmpty ? { enabled: false } : { ...labels, enabled: true },
        gridLineWidth: input.isEmpty ? 0 : input.gridLineWidth ?? 1,
        // Fixed extremes so an empty band's note has a range to span: left to derive its own from
        // data it has none of, the axis lands on extremes the band falls outside, and Highcharts
        // clips the label away entirely.
        min: input.isEmpty ? 0 : input.min ?? null,
        max: input.isEmpty ? 1 : null,
        plotBands: input.isEmpty && input.noReadingsLabel !== null
            ? [
                  {
                      from: 0,
                      to: 1,
                      color: 'transparent',
                      label: {
                          text: input.noReadingsLabel,
                          align: 'center',
                          verticalAlign: 'middle',
                          textAlign: 'center',
                          style: { fontSize: '11px', fontStyle: 'italic', color: CHART_CHROME_COLORS.emptyBandNote },
                      },
                  },
              ]
            : [],
    };
}

// Line markers are decided from the series' own shape rather than left to Highcharts' density
// heuristic, which does not fire here: a dense daily curve stays clean, while a series whose
// readings are separated by nulls turns them on, because those readings have no line segment to
// appear in and would otherwise render as nothing at all. Either way the hover state is declared,
// so the readout row's imperative setState('hover') always has a dot to show.
// No dots along a line. A continuous curve is read as a shape, and a marker on every bucket turns
// it into a row of symbols with a line behind them - which is what weight, Daily Gain and Growth
// Index all looked like, since each is broken by every Production Group boundary and (for the two
// derivations) by every implausible reading blanked out of it, so an all-or-nothing rule has
// them on permanently. Hover still gets its own enlarged marker.
const NO_MARKERS = { enabled: false, radius: 3, states: { hover: { enabled: true, radius: 5 } } };

export type ProductivityOptionsInput = {
    // The shared wrapper's Stock defaults, which the band layout below extends deliberately.
    base: Record<string, unknown>;
    chartHeight: number;
    granularity: ProductivityGranularity;
    // Now, on the Directory's own wall clock, as the axis instant it corresponds to. Passed in rather
    // than read off this machine's clock: every other instant on this axis is a farm-local reading,
    // so a browser "now" sits a whole UTC offset away from them - which shaded readings that already
    // existed on a farm east of the viewer, and ran the open Production Group's band a day into the
    // future on one west of it.
    nowMs: number;
    // The window that was actually requested, which the axis is pinned to - null before it resolves.
    range: ProductivityRange | null;
    buckets: ProductivityBucket[];
    productionGroups: ProductivityProductionGroup[];
    feedMixes: FeedMixBlock[];
    notes: NoteMarker[];
    alarms: AlarmMarker[];
    bandLayout: BandLayout;
    // A metric counts as on only if it is both switched on and expressible at this granularity.
    toggles: MetricFlags;
    emptyBands: Record<MetricBandId, boolean>;
    // The bands whose emptiness is worth stating in the band itself. Every empty band on screen while
    // some other band still holds readings - there the note is the point, since it separates "this
    // band has nothing" from "this band's readings are flat" - but only the first of them once they
    // are all empty, because then the note is a fact about the whole chart and repeating it per band
    // just fills an empty plot with the same sentence several times over.
    noteBands: readonly MetricBandId[];
    // The comparison reference currently projected onto the axis, or null when none is loaded. Both
    // how far the axis has to reach and how far its in-period numbering goes on counting - two facts
    // about one projection, so one input rather than two that can disagree.
    comparisonProjection: ComparisonProjection | null;
    // Every camera the Section had over the range, each with its own weight buckets. One entry on a
    // single-camera Section, where no camera curve is drawn at all.
    cameras: CameraSeries<WeightSeriesDay>[];
    // The cameras the reader has switched off, as Sensor ids - already resolved against `cameras` by the
    // caller (see resolveHiddenCameras), so sorted, de-duplicated, and naming cameras that exist.
    hiddenCameraSensorIds: number[];
    // The comparison halo series, empty until a reference is picked and its data has loaded.
    haloSeries: unknown[];
    eventTooltip: ProductivityEventTooltip;
    // Called on the chart's own render, for the chrome Highcharts does not own.
    onRender: (chart: Chart) => void;
};

export function buildProductivityOptions(input: ProductivityOptionsInput): Options {
    const { bandLayout, buckets, granularity, productionGroups, toggles } = input;

    const base: Record<string, unknown> = merge({}, input.base);
    // The wrapper's single-axis defaults don't apply to the band-plus-weight layout below, which
    // replaces the axis outright.
    delete base.yAxis;

    const { nowMs } = input;
    // Not the shared `texts.noData` ("Ingen data tilgængelige"): that sentence is about the chart,
    // and a band is not the chart. Read literally beside a row of water
    // Alarm markers - which are data, drawn on that very band's axis - it says the opposite of what is
    // on screen. This one is about the band's own readings, which is all it was ever claiming.
    const noReadingsLabel = i18n.global.t('views.monitoring.activeGroups.modals.productivity.bandNoReadings') as string;

    const projection = input.comparisonProjection;
    // How far right of the axis the projection reaches, whether or not that is past today.
    const projectedEndMs = projection?.endMs ?? 0;
    // Where the "past today" shading and its marker line stop - absent when the projection does not
    // reach beyond today, since a reference no longer than the period so far has no such stretch.
    const referencePastNowMs = projection !== null && projection.endMs > nowMs ? projection.endMs : null;

    const productionGroupsBox = axisBox(bandLayout.productionGroups);
    const feedMixesBox = axisBox(bandLayout.feedMixes);
    const weightBox = axisBox(bandLayout.weight);
    const growthIndexBox = axisBox(bandLayout.growthIndex);
    const waterBox = axisBox(bandLayout.water);
    const deadCountBox = axisBox(bandLayout.deadCount);

    // Built once here rather than inline per series: isolatedPointMarkers reads each one back to
    // decide whether that series needs dots at all.
    const weightSeriesData = buildWeightSeriesData(buckets, productionGroups, granularity);
    const dailyGainSeriesData = buildDailyGainSeriesData(buckets, productionGroups, granularity);
    const growthIndexSeriesData = buildGrowthIndexSeriesData(buckets, productionGroups, granularity);

    // Every camera curve this Section can draw: one per camera per metric a camera reports, gated by two
    // things only - its metric's own toggle, so the chip that draws the aggregate draws the cameras under
    // it, and the reader having switched that camera off.
    //
    // Switched off means hidden, not removed: the series keeps its id and data, so bringing it back is a
    // redraw rather than a refetch and a camera's colour cannot shift under the reader.
    //
    // Nothing at all on a single-camera Section, where the aggregate IS that camera.
    const multiCamera = drawsCameraSeries(input.cameras);
    const hiddenCameras = input.hiddenCameraSensorIds;
    // What the cameras still on the plot are drawn as - one answer for all of them, hidden ones included,
    // since a series nobody can see is not styled either way. See cameraEmphasis.
    const emphasis = cameraEmphasis(input.cameras.length - hiddenCameras.length);
    const cameraMetricKeys = METRIC_KEYS.filter(isCameraMetric);
    const cameraMetricSeries = multiCamera
        ? cameraMetricKeys.flatMap((key) =>
              input.cameras.map((camera, index) => ({
                  id: cameraSeriesKey(PRODUCTIVITY_METRICS[key].seriesId, index),
                  type: 'line' as const,
                  name: cameraSeriesName(i18n.global.t(PRODUCTIVITY_METRICS[key].labelKey) as string, camera, index),
                  data: isolatedPointMarkers(buildCameraMetricSeriesData(camera.buckets, productionGroups, key, granularity)),
                  yAxis: PRODUCTIVITY_AXIS[PRODUCTIVITY_METRICS[key].axis],
                  visible: toggles[key] && !hiddenCameras.includes(camera.sensor_id),
                  ...cameraSeriesStyle(index, key, emphasis),
                  // Wins over the style's own markers-off, so a camera curve gets the same enlarged dot
                  // under the pointer every other line series here has - for the reader tracing one
                  // camera line, not for the readout row, which never addresses a camera.
                  marker: NO_MARKERS,
              }))
          )
        : [];

    // What the navigator strip draws: the first of these that actually holds readings, in the order
    // a reader would navigate by. Weight is the curve the range is usually chosen against; water
    // stands in where there is no weight Sensor; Growth Index is the last resort because it is the
    // one series a Section can never be short of while it has a camera at all.
    // Raw arrays rather than the marker-annotated ones: the strip has no markers to annotate.
    const waterActualSeriesData = buildWaterActualSeriesData(buckets, productionGroups, waterActualKey(granularity), granularity);
    const hasReadings = (data: Array<[number, number | null]>) => data.some(([, value]) => value !== null);
    const navigatorCandidates: Array<[Array<[number, number | null]>, string]> = [
        [weightSeriesData, PRODUCTIVITY_METRICS.weight.color],
        [waterActualSeriesData, PRODUCTIVITY_METRICS.water.color],
        [growthIndexSeriesData, PRODUCTIVITY_METRICS.growthIndex.color],
    ];
    const [navigatorSeriesData, navigatorSeriesColor] = navigatorCandidates.find(([data]) => hasReadings(data)) ?? navigatorCandidates[0];

    // Shared by the water columns so they overlap at exactly the same x position and width (see the
    // series comment below) - single-sourced here rather than copy-pasted per series so the padding
    // can never drift out of alignment between them.
    const waterColumnLayout = {
        yAxis: PRODUCTIVITY_AXIS.water,
        grouping: false,
        pointPadding: 0,
        groupPadding: 0.1,
        borderRadius: 1,
        pointRange: waterColumnRange(buckets, granularity),
    };

    // How metered water is drawn, which is the one thing about this chart that changes shape with
    // the granularity rather than merely changing unit.
    //
    // At day it is a column: one reading per bucket, read against the Water Norm column behind it,
    // and the pair only makes sense as two overlapping blocks of the same width. At hour there is no
    // norm to read it against (it is published per calendar day) and ~1900 buckets leave each column
    // under a pixel wide, so the band reads as noise - it becomes the filled spline the backend's own
    // realtime water chart uses for its hourly view, fading out downwards so a dense trace does not
    // read as a solid slab. The smoothing does interpolate between hours, which is the one place this
    // chart draws something it was not told; accepted deliberately for the hourly trace, where the
    // shape of the day is what is being read rather than any single hour's total.
    const waterActualShape =
        granularity === 'hour'
            ? { type: 'areaspline' as const, yAxis: PRODUCTIVITY_AXIS.water, lineWidth: 1, fillColor: verticalFade(PRODUCTIVITY_METRICS.water.color), marker: NO_MARKERS }
            : { ...waterColumnLayout, type: 'column' as const };

    // The requested window's own width in time, which is what decides how close two Note or Alarm
    // pins can be before they are drawn as one - see clusterEvents. Falls back to the span the
    // loaded buckets cover for a range that has not resolved yet.
    const bucketTimestamps = buckets.map(bucketTimestamp);
    const loadedSpanMs = bucketTimestamps.length > 1 ? Math.max(...bucketTimestamps) - Math.min(...bucketTimestamps) : 0;
    const rangeSpanMs = input.range !== null ? Date.parse(input.range.to) - Date.parse(input.range.from) : loadedSpanMs;

    // One line per Production Group start. Drawn only when the range holds more than one: with a single
    // period the axis counts from its start and nothing restarts, so a line there would mark a
    // boundary that carries no meaning.
    const productionGroupBoundaryPlotLines: Array<Record<string, unknown>> =
        productionGroups.length < 2
            ? []
            : productionGroups.map((productionGroup) => ({
                  id: `productivity-productionGroup-start-${productionGroup.production_group_id}`,
                  value: Date.parse(productionGroup.start),
                  color: CHART_AXIS_RULE_COLORS.productionGroupBoundary,
                  width: 1.5,
                  zIndex: 3,
              }));

    // Cadence is chosen from the span, so the requested window changing is exactly when the calendar
    // rules have to be reconsidered.
    const calendarRulePlotLines: Array<Record<string, unknown>> =
        input.range === null ? [] : buildCalendarRulePlotLines(Date.parse(input.range.from), Date.parse(input.range.to), CHART_AXIS_RULE_COLORS.calendarRule);

    return merge(base, {
        // marginLeft reserves a fixed gutter for the band axes' own labels so every band's plot
        // area starts at the same x - without it Highcharts sizes the gutter to whichever band
        // happens to carry the widest number, and the bands visibly shift sideways as toggles
        // change what is on screen.
        chart: {
            height: input.chartHeight,
            backgroundColor: 'transparent',
            spacingLeft: 0,
            spacingRight: 0,
            marginLeft: 56,
            marginRight: 56,
            // `function` rather than an arrow: Highcharts calls the handler with the chart as
            // `this`, and the ref holding the instance is only assigned after the first render.
            events: {
                render(this: Chart) {
                    input.onRender(this);
                },
            },
        },
        exporting: { enabled: false },
        // Range selection lives in the panel's own toolbar (see the template), not in the stock
        // range selector: the buttons refetch from the server rather than zoom loaded data, so they
        // were already overriding every part of the selector's behaviour, and its SVG-rendered
        // buttons could not be made to match the granularity group they sit beside.
        rangeSelector: { enabled: false },
        scrollbar: { enabled: false },
        // The scrub strip: drags a window across the selected range without altering the range
        // selection above - Highcharts' navigator only zooms the already-loaded data client-side,
        // it never triggers a refetch, so it can never disagree with rangeOption.
        navigator: {
            enabled: true,
            // Tall enough for the curve inside it to have a shape. At 28px the strip drew the whole
            // range's weight as a few pixels of wobble, which is unreadable as an overview and gives
            // the drag handles almost nothing to grab - and the strip is how the reader moves around
            // a twelve-month range, so it is worth the height. Kept in step with CHART_NON_PLOT_PX,
            // which subtracts this and the margin below from what the bands get to divide up.
            height: 44,
            margin: 16,
            maskFill: CHART_CHROME_COLORS.navigatorMask,
            outlineColor: CHART_CHROME_COLORS.axisLine,
            handles: { backgroundColor: CHART_CHROME_COLORS.navigatorHandleFill, borderColor: CHART_CHROME_COLORS.navigatorHandleBorder },
            // Clamped to the same window the main axis is pinned to. Left to itself the strip spans
            // the extremes of everything on the chart, and the Production Group context bars are not
            // clipped to the requested range - so picking "3 months" on a Section whose Production Group
            // began a year earlier drew a year-wide strip, three quarters of it empty, inviting the
            // reader to drag into months for which nothing was ever fetched.
            xAxis: {
                min: input.range !== null ? Date.parse(input.range.from) : undefined,
                max: input.range !== null ? Math.max(Date.parse(input.range.to), projectedEndMs) : undefined,
                labels: { style: { color: CHART_CHROME_COLORS.axisLabel, fontSize: '10px' } },
            },
            // The strip defaults to whichever series happens to be first, which here is a Study
            // Period context bar - so it drew a flat slab and told the reader nothing about where in
            // the range the data actually is. Weight is the curve people navigate by; water stands in
            // where weight is unavailable (no weight Sensor), since a strip showing
            // nothing is worse than one showing the other primary read.
            series: {
                type: 'areaspline',
                data: navigatorSeriesData,
                color: navigatorSeriesColor,
                lineWidth: 1.5,
                fillOpacity: 0.14,
                marker: { enabled: false },
            },
        },
        legend: { enabled: false },
        xAxis: {
            // Pinned to the window that was actually requested. Left to fit its data, the axis
            // stretches to cover the Production Group context bars, which are not clipped to the range -
            // so picking "3 months" on a Section whose Production Group started a year ago drew a
            // year-long axis with three months of readings crammed into its right-hand end, and
            // nothing on screen said the range control had done anything at all.
            min: input.range !== null ? Date.parse(input.range.from) : null,
            // A loaded reference re-projected onto the current period can run past the range's end;
            // the axis has to reach it or the overlay is cut off mid-curve.
            max: input.range !== null ? Math.max(Date.parse(input.range.to), projectedEndMs) : null,
            // Highcharts *Stock* defaults this to true, which squeezes stretches with no points out
            // of the axis entirely - the exact opposite of what this chart is for. A Section
            // standing empty between two Production Groups, or a Sensor that stopped reporting days
            // ago, has to read as real empty width running up to now; an ordinal axis silently
            // closes both gaps and puts the two sides of them next to each other. It also decouples
            // pixel distance from elapsed time, which the tick positioner's own spacing rules
            // depend on.
            ordinal: false,
            tickPositioner: productivityTickPositioner(productionGroups, nowMs, projection),
            // rotation 0 with autoRotation off: the tick positioner already thins the ticks to a
            // count that fits (see tickStrideMsFor), so a label never needs to tilt to make room -
            // and Highcharts' own fallback of tilting them to -45 would undo that thinning's whole
            // point. The shared wrapper's default is -90, hence overriding both here.
            labels: {
                formatter: productivityAxisLabelFormatter(productionGroups, nowMs, projection),
                rotation: 0,
                autoRotation: undefined,
                style: { fontSize: '11px', color: CHART_CHROME_COLORS.labelText },
            },
            // Highcharts Stock draws a crosshair of its own by default. This chart already has one -
            // the plot line the readout row moves - and the two are only ever in the same place
            // until the reader pins the readout, at which point the chart shows two vertical lines
            // and no way to tell which of them the figures belong to.
            crosshair: false,
            lineColor: CHART_CHROME_COLORS.axisLine,
            tickColor: CHART_CHROME_COLORS.axisLine,
            // No tick grid: the calendar rules below replace it. Highcharts' own ticks land where
            // the label algorithm puts them, which is not a calendar boundary and so measures
            // nothing a reader of this chart is asking about.
            gridLineWidth: 0,
            // A reference that runs longer than the current Production Group keeps drawing past today
            // once re-projected onto it - the region right of "now" carries a marker line and
            // faint shading so it is never mistaken for a measurement, since it holds only
            // reference data. Absent whenever no loaded reference reaches that far.
            //
            // Alongside it, a light line wherever a Production Group begins. Week-in-period numbering
            // restarts at each one, so a range covering two of them puts "Uge 1" on the axis twice
            // and nothing on the axis says why - it reads as a mistake rather than as a new batch
            // starting its own count. These lines mark exactly where the count goes back to one.
            plotLines: calendarRulePlotLines.concat(productionGroupBoundaryPlotLines).concat(
                referencePastNowMs
                    ? [
                          {
                              id: 'productivity-reference-now',
                              value: nowMs,
                              color: CHART_CHROME_COLORS.referenceNowRule,
                              dashStyle: 'Dash',
                              width: 1,
                              zIndex: 4,
                              label: {
                                  text: i18n.global.t('views.monitoring.activeGroups.modals.productivity.compareFutureReferenceLabel') as string,
                                  rotation: 0,
                                  y: 12,
                                  style: { fontSize: '10px', color: CHART_CHROME_COLORS.labelText },
                              },
                          },
                      ]
                    : []
            ),
            plotBands: referencePastNowMs !== null ? [{ from: nowMs, to: referencePastNowMs, color: CHART_CHROME_COLORS.referencePastNowShading, zIndex: 0 }] : [],
        },
        // Production Group and Feed Mix context bands on top (thin, each its own axis so the
        // xrange series never share scale with the bands below them - a shared scale would let a
        // Production Group bar's own extent drive the axis the data below it is drawn on); weight and
        // Daily Gain share the next band (same box, opposite axes); water actual and Water Norm
        // share their own band (same box, same axis - norm drawn behind actual, not opposite axes,
        // since the whole point is reading both against the same scale); Growth Index gets its own
        // sub-band with its 100 baseline marked. Every top/height below comes from
        // computeBandLayout, never hardcoded. Note and Alarm pins anchor to the Production Group band's
        // own axis - by construction it always has at least one Production Group whenever there is
        // a pin to draw (the backend's own empty-state gate).
        //
        // The order of this array is PRODUCTIVITY_AXIS, which is how every series and halo addresses one.
        yAxis: [
            // Chrome always hidden - the xrange series is its own label - regardless of whether
            // the band is currently showing (productionGroups is empty rather than the band merely
            // being collapsed by a toggle, since this band isn't user-toggleable).
            { ...productionGroupsBox, visible: false, min: 0, max: 1, gridLineWidth: 0 },
            { ...feedMixesBox, visible: false, min: 0, max: 1, gridLineWidth: 0 },
            dataBandAxis({
                box: weightBox,
                // Weight is expressible at every granularity, so this collapses only when the reader
                // turns it off - the box stays either way, because Daily Gain shares it.
                visible: weightBox.visible && toggles.weight,
                title: bandTitle(metricCaption('weight', granularity)),
                isEmpty: input.emptyBands.weight,
                noReadingsLabel: input.noteBands.includes('weight') ? noReadingsLabel : null,
                // No zero anchor, unlike the column bands below. A pig never weighs nothing, so the
                // stretch between zero and the lightest reading holds no information and the whole
                // curve is flattened into whatever is left - and this band is read for the shape of
                // that curve. Same for Daily Gain, which is a rate rather than a stock. `floor`
                // rather than `min` for the same reason as the Growth Index band below: it stops a
                // negative reading scaling the axis without pinning a healthy one to zero.
                own: { floor: 0 },
            }),
            dataBandAxis({
                box: weightBox,
                visible: weightBox.visible && toggles.dailyGain,
                // The one band axis that keeps Stock's right-hand default: it shares its box with
                // weight above, so putting both scales on the same side would stack two sets of
                // numbers on top of each other.
                opposite: true,
                // One grid for the shared box, drawn by the weight axis - a second set of lines at
                // this axis' own ticks would cross it at unrelated heights.
                gridLineWidth: 0,
                title: bandTitle(metricCaption('dailyGain', granularity), true),
                isEmpty: false,
                noReadingsLabel: null,
            }),
            dataBandAxis({
                box: waterBox,
                visible: waterBox.visible && (toggles.water || toggles.waterNorm),
                // Right-hand side, because this band shares a plot box with weight above it (see
                // FUSED_WITH_BAND_ABOVE). On the left its numbers would run into weight's own
                // gutter and its caption - drawn just above the axis top - would land inside the
                // weight curve rather than in a gap that no longer exists.
                opposite: true,
                title: bandTitle(metricCaption('water', granularity), true, true),
                isEmpty: input.emptyBands.water,
                noReadingsLabel: input.noteBands.includes('water') ? noReadingsLabel : null,
                // Columns, so this one keeps its zero: a bar's length is only readable against a
                // zero baseline, and cutting the axis short would exaggerate every difference
                // between one day's intake and the next.
                min: 0,
                own: { minPadding: 0 },
            }),
            dataBandAxis({
                box: growthIndexBox,
                visible: growthIndexBox.visible,
                title: bandTitle(metricCaption('growthIndex', granularity)),
                isEmpty: input.emptyBands.growthIndex,
                noReadingsLabel: input.noteBands.includes('growthIndex') ? noReadingsLabel : null,
                own: {
                    // Growth Index is read against its own 100 line, not against zero - a Section
                    // running at 95 against one at 105 is the whole question, and on a zero-anchored
                    // axis those two sit a few pixels apart. The soft bounds keep the 100 line on
                    // screen even where a range never crosses it.
                    softMin: 95,
                    softMax: 105,
                    // A floor rather than a min: this band has to survive a nonsense reading without
                    // being scaled by it. A Growth Index is a percentage of a growth target and cannot
                    // be negative, but the series does carry the occasional large negative where the
                    // underlying weight went backwards - and one of those takes the axis down with it,
                    // flattening a year of real readings into a line. `floor` bounds the *computed*
                    // extreme, so a healthy Section still gets an axis fitted to its own data.
                    floor: 0,
                    // 100 is Growth Index's neutral reference: above it the Section is ahead of its
                    // growth target, below it behind - drawn in so a crossing reads at a glance
                    // against the weight curve in the band above, without a tooltip.
                    plotLines: [
                        {
                            value: 100,
                            color: CHART_REFERENCE_COLORS.line,
                            dashStyle: 'ShortDash',
                            width: 2,
                            zIndex: 5,
                            label: {
                                text: i18n.global.t('views.monitoring.activeGroups.modals.productivity.indexReferenceLabel') as string,
                                // Right-hand end of the line, not the left: the axis prints its own
                                // "100" tick at exactly this height, and a left-aligned label landed
                                // four pixels from it - the two ran together into "100 Mål (100)",
                                // which reads as one broken string rather than as a tick and the name
                                // of a line. Nothing else occupies this band's right-hand end.
                                align: 'right',
                                verticalAlign: 'bottom',
                                x: -4,
                                y: -2,
                                style: { fontSize: '10px', fontWeight: '600', color: CHART_REFERENCE_COLORS.label },
                            },
                        },
                    ],
                },
            }),
            dataBandAxis({
                box: deadCountBox,
                visible: deadCountBox.visible,
                title: bandTitle(metricCaption('deadCount', granularity)),
                isEmpty: input.emptyBands.deadCount,
                noReadingsLabel: input.noteBands.includes('deadCount') ? noReadingsLabel : null,
                // Same zero baseline as the water band above, for the same reason.
                min: 0,
                own: { allowDecimals: false, softMax: 1 },
            }),
        ],
        // No floating tooltip for the data series - the fixed readout row above the chart takes
        // over that job, and unlike a tooltip it never covers the bands it describes. Note and water
        // Alarm pins are the one exception, and the formatter returning false for every other
        // series is what keeps them the exception: a pin is a single glyph carrying a date and a
        // message that appear nowhere else, so without this it says only that *something* happened.
        // The context bars need no such help - each already draws its own name inside itself.
        tooltip: {
            enabled: true,
            // Drawn inside the chart's own SVG. Highcharts' `outside` variant appends its container
            // to <body> at a z-index of its own choosing, which lands underneath this fullscreen
            // overlay - the tooltip is built, positioned and then completely invisible.
            outside: false,
            useHTML: true,
            borderWidth: 0,
            shadow: false,
            backgroundColor: 'transparent',
            style: { fontSize: '11px' },
            positioner: input.eventTooltip.positioner,
            formatter: input.eventTooltip.formatter,
        },
        plotOptions: {
            series: {
                // Hovering any one series must not fade the rest. Highcharts' default `inactive`
                // state dims every other series to pick one out, which on a stacked-band chart
                // greys out most of the panel the moment the cursor crosses a context bar - and
                // this chart is read as a whole, not one series at a time.
                states: { inactive: { opacity: 1 } },
                // The data series answer to the readout row, not to the pointer: it is the row that
                // decides which bucket is being described and marks it. Left tracking, they also
                // light up a point under the cursor, which contradicts the row outright the moment
                // it is pinned. The two event series opt back in below - a Note pin and an Alarm
                // mark carry a message that appears nowhere else, so hover is the only way to read
                // them, and inheriting the blanket opt-out left the Alarm marks completely inert.
                enableMouseTracking: false,
                // Every bucket is already an aggregate, cut at the granularity the reader asked for
                // by the read that produced it. Highstock's own grouping is on by default and is a
                // second aggregation on top of that: at hourly it folded ~1900 buckets into 153
                // twelve-hour groups, summed the water columns while the readout row went on quoting
                // the single hour, and moved every point's x onto a group anchor - so the cursor's
                // own lookup, which finds a point by the bucket's instant, matched nothing and no
                // on-chart label was drawn for any series. Day hid all of it, its bucket count being
                // low enough for grouping to be a no-op. Off, as every sibling chart in this folder
                // already sets it.
                dataGrouping: { enabled: false },
            },
            // Highcharts holds a series' mouseOut until the pointer enters another series or leaves
            // the plot outright, which on these two sparse series means a tooltip stays open while
            // the cursor wanders the whole chart. Turning sticky tracking off fixes that for the
            // Alarm scatter, whose markers have real hit areas - but it stops `flags` responding to
            // hover at all, their tracking being the series tracker rather than per-point. So the
            // pins keep sticky tracking and the event tooltip's own hideWhenAway closes them instead.
            flags: { enableMouseTracking: true },
            scatter: { enableMouseTracking: true, stickyTracking: false },
        },
        // Marker states are declared per line series below (see NO_MARKERS) so the readout row's
        // imperative point.setState('hover') calls have a dot to show - without this, a series whose
        // marker is disabled at rest stays dot-less on hover.
        series: [
            buildProductionGroupSeries(productionGroups, nowMs),
            buildFeedMixSeries(input.feedMixes, nowMs),
            ...buildEventSeries(input.notes, input.alarms, rangeSpanMs),
            {
                id: PRODUCTIVITY_METRICS.weight.seriesId,
                type: 'line',
                name: i18n.global.t(PRODUCTIVITY_METRICS.weight.labelKey) as string,
                data: isolatedPointMarkers(weightSeriesData),
                yAxis: PRODUCTIVITY_AXIS.weight,
                visible: toggles.weight,
                // Explicit (rather than the auto-palette default) so the comparison halo can share
                // this exact hue - see the registry's own colours.
                color: PRODUCTIVITY_METRICS.weight.color,
                marker: NO_MARKERS,
                // Heavier and above its own cameras where there are any, and untouched where there
                // are none - see aggregateEmphasis.
                ...aggregateEmphasis(multiCamera),
            },
            {
                id: PRODUCTIVITY_METRICS.dailyGain.seriesId,
                type: 'line',
                name: i18n.global.t(PRODUCTIVITY_METRICS.dailyGain.labelKey) as string,
                data: isolatedPointMarkers(dailyGainSeriesData),
                yAxis: PRODUCTIVITY_AXIS.dailyGain,
                visible: toggles.dailyGain,
                dashStyle: 'ShortDash',
                color: PRODUCTIVITY_METRICS.dailyGain.color,
                marker: NO_MARKERS,
                // Heavier and above its own cameras where there are any, and untouched where there
                // are none - see aggregateEmphasis.
                ...aggregateEmphasis(multiCamera),
            },
            // Water Norm drawn first (behind, muted) then water actual (in front, primary colour) -
            // grouping disabled and identical padding on every water column (waterColumnLayout) so
            // they overlap at exactly the same x position and width instead of dodging side by side.
            // A deficit shows as the norm column's colour poking above the (shorter) actual column;
            // the over-norm columnrange on top explicitly highlights the opposite direction rather
            // than letting the actual column silently cover the norm underneath it. All of which is
            // a day-granularity arrangement: at hour there is no norm at all and the actual takes
            // the filled-spline shape instead - see waterActualShape.
            {
                ...waterColumnLayout,
                id: PRODUCTIVITY_METRICS.waterNorm.seriesId,
                type: 'column',
                name: i18n.global.t(PRODUCTIVITY_METRICS.waterNorm.labelKey) as string,
                data: buildWaterNormSeriesData(buckets, productionGroups, granularity),
                visible: toggles.waterNorm,
                color: PRODUCTIVITY_METRICS.waterNorm.color,
                zIndex: 1,
            },
            {
                ...waterActualShape,
                id: PRODUCTIVITY_METRICS.water.seriesId,
                name: i18n.global.t(PRODUCTIVITY_METRICS.water.labelKey) as string,
                data: waterActualSeriesData,
                visible: toggles.water,
                color: PRODUCTIVITY_METRICS.water.color,
                zIndex: 2,
            },
            {
                ...waterColumnLayout,
                type: 'columnrange',
                name: i18n.global.t('views.monitoring.activeGroups.stream.waterOverNorm') as string,
                data: buildWaterOverNormSeriesData(buckets),
                visible: toggles.water,
                color: CHART_SERIES_COLORS.waterOverNorm,
                zIndex: 3,
            },
            {
                id: PRODUCTIVITY_METRICS.growthIndex.seriesId,
                type: 'line',
                name: i18n.global.t(PRODUCTIVITY_METRICS.growthIndex.labelKey) as string,
                data: isolatedPointMarkers(growthIndexSeriesData),
                yAxis: PRODUCTIVITY_AXIS.growthIndex,
                visible: toggles.growthIndex,
                color: PRODUCTIVITY_METRICS.growthIndex.color,
                marker: NO_MARKERS,
                // Heavier and above its own cameras where there are any, and untouched where there
                // are none - see aggregateEmphasis.
                ...aggregateEmphasis(multiCamera),
            },
            {
                id: PRODUCTIVITY_METRICS.deadCount.seriesId,
                type: 'column',
                name: i18n.global.t(PRODUCTIVITY_METRICS.deadCount.labelKey) as string,
                data: buildDeadCountSeriesData(buckets, productionGroups, granularity),
                yAxis: PRODUCTIVITY_AXIS.deadCount,
                visible: toggles.deadCount,
                color: PRODUCTIVITY_METRICS.deadCount.color,
                // A day's tally, so a day wide - at hourly the axis' own closest pair of points is an
                // hour apart, and left to infer its width from that the column claimed one hour out
                // of the twenty-four it is about, drawing as a sliver under a pixel wide.
                pointRange: metricColumnRange('deadCount', granularity),
                // No minPointLength: a day with no deaths is the overwhelmingly common case, and
                // forcing every zero to a visible stub draws a continuous red dashed rule across
                // the band that reads as a running deadCount level rather than as its absence.
                borderRadius: 1,
            },
            // The per-camera curves: each behind the aggregate it contributed to (cameraSeriesStyle's own
            // zIndex) and in front of any reference halo of its own.
            ...cameraMetricSeries,
            // The comparison reference halo, one series per metric. Drawn last (and at zIndex 0
            // within each series) so a hidden/empty reference never disturbs the primary series' own
            // stacking, and a loaded one draws behind them.
            ...input.haloSeries,
        ],
    // The cast is Highcharts' own `Options`, which describes a single-axis chart: its `yAxis` and
    // `series` members are narrower than the seven-axis array and the mixed series list a
    // stacked-band Stock chart needs, and neither can be expressed in that type at all.
    }) as unknown as Options;
}
